import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Paper,
  Avatar,
  Divider
} from '@mui/material'
import { AdminPanelSettings as AdminIcon, Security as SecurityIcon } from '@mui/icons-material'
import { useAdmin } from '../../contexts/AdminContext'

// ...existing code...

const AdminLogin: React.FC = () => {
  const navigate = useNavigate()
  const { login } = useAdmin()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email || !validateEmail(formData.email)) {
      setError('Please enter a valid email address')
      return
    }
    if (!formData.password) {
      setError('Please enter your password')
      return
    }

    setIsLoading(true)

    try {
      // Use AdminContext's login function
      await login(formData.email, formData.password)

      // Navigate to admin dashboard
      navigate('/admin')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDemoLogin = async () => {
    const demoEmail = 'tu@tu.com'
    const demoPassword = '123456789'
    setFormData({ email: demoEmail, password: demoPassword })
    setError('')
    setIsLoading(true)
    try {
      await login(demoEmail, demoPassword)
      navigate('/admin')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 64, height: 64 }}>
              <AdminIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h4" gutterBottom>
              Master Admin Login
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Access the system-wide administration dashboard
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Admin Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <SecurityIcon />}
            >
              {isLoading ? 'Signing In...' : 'Sign In as Admin'}
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={handleDemoLogin}
              disabled={isLoading}
              sx={{ mb: 2 }}
            >
              Demo Login (tu@tu.com)
            </Button>
          </Box>

          <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <strong>Demo Credentials:</strong><br />
              Email: tu@tu.com<br />
              Password: 123456789
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}

export default AdminLogin
